/**
 * A single-measure ranked bar with the client emphasised (2026-09-08).
 *
 * The workhorse of the report's visual layer: four sections are "a list of
 * things ranked by one number", and they should all look and read the same way
 * rather than each inventing a chart. Used by Competitive Sets (named and
 * AI-surfaced), Surfaced Supplier Detail, and Top Journals.
 *
 * FORM: emphasis (see the `dataviz` form table — "one series is the point, rest
 * are context"). One hue for the highlighted row, de-emphasis grey for the
 * rest. Colour never encodes rank, so re-sorting or filtering cannot repaint
 * the field.
 */
import { CONTEXT, TARGET } from './palette';

export interface RankedBarDatum {
  label: string;
  value: number;
  /** Renders in the accent hue rather than context grey. */
  highlight?: boolean;
  /** Optional second line under the label (a domain, a supplier type). */
  sublabel?: string | null;
  /** Extra detail for the hover tooltip only. */
  tooltip?: string;
}

const BAR_H = 15;
const BAR_GAP = 6;
const LABEL_W = 176;
const TRACK_W = 300;
const CHART_W = LABEL_W + TRACK_W + 64;
const LABEL_MAX = 24;

export function RankedBarList({
  data,
  hue = TARGET,
  valueSuffix = '',
  caption,
  maxRows,
}: {
  data: RankedBarDatum[];
  /** Accent for highlighted rows. Defaults to the client's blue. */
  hue?: string;
  valueSuffix?: string;
  caption?: string;
  /** Cut the list, but never drop a highlighted row — it is appended instead. */
  maxRows?: number;
}): JSX.Element | null {
  if (data.length === 0) return null;

  let rows = maxRows ? data.slice(0, maxRows) : data;
  let appended = false;
  if (maxRows && !rows.some((r) => r.highlight)) {
    const hit = data.find((r) => r.highlight);
    if (hit) {
      rows = [...rows, hit];
      appended = true;
    }
  }

  const max = Math.max(1, ...rows.map((r) => r.value));
  const rowH = BAR_H + BAR_GAP;
  const chartH = rows.length * rowH;

  return (
    <figure className="ranked-bars">
      <svg
        width="100%"
        viewBox={`0 0 ${CHART_W} ${chartH}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label={caption ?? 'Ranked bar chart'}
      >
        {rows.map((r, i) => {
          const y = i * rowH;
          const w = Math.max(r.value > 0 ? 2 : 0, (r.value / max) * TRACK_W);
          const label =
            r.label.length > LABEL_MAX
              ? `${r.label.slice(0, LABEL_MAX - 1)}…`
              : r.label;
          return (
            <g key={`${r.label}-${i}`}>
              <text
                x={LABEL_W - 8}
                y={y + BAR_H / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className={
                  r.highlight ? 'chart-bar-label is-target' : 'chart-bar-label'
                }
              >
                {label}
              </text>
              <rect
                x={LABEL_W}
                y={y}
                width={TRACK_W}
                height={BAR_H}
                rx={3}
                className="chart-bar-track"
              />
              <rect
                x={LABEL_W}
                y={y}
                width={w}
                height={BAR_H}
                rx={3}
                fill={r.highlight ? hue : CONTEXT}
              >
                <title>
                  {r.tooltip ??
                    `${r.label}: ${r.value}${valueSuffix}${r.sublabel ? ` — ${r.sublabel}` : ''}`}
                </title>
              </rect>
              <text
                x={LABEL_W + Math.max(w, 2) + 6}
                y={y + BAR_H / 2}
                dominantBaseline="middle"
                className={
                  r.highlight ? 'chart-bar-value is-target' : 'chart-bar-value'
                }
              >
                {r.value}
                {valueSuffix}
              </text>
            </g>
          );
        })}
      </svg>
      {(caption || appended) && (
        <figcaption className="muted ranked-bars-caption">
          {caption}
          {appended && ' Your row is appended below the cut for comparison.'}
        </figcaption>
      )}
    </figure>
  );
}
